import { PartnerType } from 'legacy/lut/classes/models/PartnerType'

import {
  I621Form,
  IFinancialCockpitEltDocument,
  IFinancialCockpitEltDocuments,
  IFinancialCockpitQbrDocument,
  IFinancialCockpitQbrDocuments,
} from '../../api/types'

import { normalizePartnerId } from './normalizePartnerId'
import { resolvePartnerRtm } from './resolvePartnerRtm'
import { sortEltDocuments } from './sortEltDocuments'
import { sortQbrDocuments } from './sortQbrDocuments'

export interface IPartnerDocumentSection {
  partnerId: string
  partnerName: string
  rtm: PartnerType | null
  eltDocuments: IFinancialCockpitEltDocument[]
  qbrDocuments: IFinancialCockpitQbrDocument[]
}

const dedupeBy = <T>(items: T[], getKey: (item: T, index: number) => string): T[] => {
  const seen = new Set<string>()
  const result: T[] = []

  items.forEach((item, index) => {
    const key = getKey(item, index)

    if (!seen.has(key)) {
      seen.add(key)
      result.push(item)
    }
  })

  return result
}

const getEltDocumentKey = (document: IFinancialCockpitEltDocument, index: number): string => {
  if (document.EntryId > 0) {
    return `entry:${document.EntryId}`
  }

  if (document.UID) {
    return `uid:${document.UID}`
  }

  return `unsaved:${index}`
}

const getQbrDocumentKey = (document: IFinancialCockpitQbrDocument): string =>
  `entry:${document.EntryId}`

const groupByNormalizedPartnerId = <TGroup extends { PartnerId: string }, TDoc>(
  groups: TGroup[],
  getDocuments: (group: TGroup) => TDoc[],
  dedupe: (documents: TDoc[]) => TDoc[]
): Map<string, TDoc[]> => {
  const byPartnerId = new Map<string, TDoc[]>()

  groups.forEach((group) => {
    const partnerId = normalizePartnerId(group.PartnerId)
    const existing = byPartnerId.get(partnerId) ?? []
    byPartnerId.set(partnerId, [...existing, ...getDocuments(group)])
  })

  byPartnerId.forEach((documents, partnerId) => {
    byPartnerId.set(partnerId, dedupe(documents))
  })

  return byPartnerId
}

export const buildPartnerDocumentSections = (
  partnerData: I621Form[],
  eltGroups: IFinancialCockpitEltDocuments[],
  qbrGroups: IFinancialCockpitQbrDocuments[]
): IPartnerDocumentSection[] => {
  const eltByPartnerId = groupByNormalizedPartnerId(
    eltGroups,
    (group) => (Array.isArray(group.ELTDocuments) ? group.ELTDocuments : []),
    (documents) => dedupeBy(documents, getEltDocumentKey)
  )
  const qbrByPartnerId = groupByNormalizedPartnerId(
    qbrGroups,
    (group) => (Array.isArray(group.QBRDocuments) ? group.QBRDocuments : []),
    (documents) => dedupeBy(documents, getQbrDocumentKey)
  )
  const partnerById = new Map(
    partnerData.map((partner) => [normalizePartnerId(partner.PartnerId), partner])
  )

  const orderedPartnerIds: string[] = []
  const seenPartnerIds = new Set<string>()

  const addPartnerId = (partnerId: string) => {
    if (!seenPartnerIds.has(partnerId)) {
      seenPartnerIds.add(partnerId)
      orderedPartnerIds.push(partnerId)
    }
  }

  partnerData.forEach((partner) => addPartnerId(normalizePartnerId(partner.PartnerId)))
  eltByPartnerId.forEach((_documents, partnerId) => addPartnerId(partnerId))
  qbrByPartnerId.forEach((_documents, partnerId) => addPartnerId(partnerId))

  return orderedPartnerIds.map((partnerId) => {
    const partner = partnerById.get(partnerId)

    return {
      partnerId,
      partnerName: partner?.PartnerName || partnerId,
      rtm: resolvePartnerRtm(partner),
      eltDocuments: sortEltDocuments(eltByPartnerId.get(partnerId) ?? []),
      qbrDocuments: sortQbrDocuments(qbrByPartnerId.get(partnerId) ?? []),
    }
  })
}
